import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "../../components/ui/modal";
import Input from "../../components/form/input/InputField";
import ListboxComponent from "../../components/common/ListboxComponent";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import axios from "axios";

type EditCompanyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  state: {
    company_id: string;
  };
};

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  isOpen,
  onClose,
  state,
}) => {
  const navigate = useNavigate();
  const { company_id } = state;

  const [company_name, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone_number, setPhoneNumber] = useState("");
  const [contact_number, setContactNumber] = useState("");
  const [address, setAddress] = useState("");
  const [gst_type, setGstType] = useState("");
  const [company_logo, setCompanyLogo] = useState<File | null>(null);
  const [company_logo_url, setCompanyLogoUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    company_name?: string;
    email?: string;
    phone_number?: string;
    contact_number?: string;
    address?: string;
    gst_type?: string;
    company_logo?: string;
  }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const token = sessionStorage.getItem("access_token");

  // Use useMemo to memoize the gstTypes array
  const gstTypes = useMemo(
    () => [
      { id: "1", type_name: "CGST+SGST" },
      { id: "2", type_name: "IGST" },
    ],
    []
  );

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (!token || !company_id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/companies`,
          {
            params: { company_id },
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.status === 200 && response.data.data.length > 0) {
          const companyData = response.data.data[0];
          setCompanyName(companyData.company_name || "");
          setEmail(companyData.email || "");
          setPhoneNumber(companyData.phone_number || "");
          setContactNumber(companyData.contact_number || "");
          setAddress(companyData.address || "");
          const gstTypeValue = companyData.gst_type?.toString() || "";
          if (
            gstTypeValue &&
            !gstTypes.find((type) => type.id === gstTypeValue)
          ) {
            setErrors((prev) => ({
              ...prev,
              gst_type: "Invalid GST type received from server",
            }));
          }
          setGstType(gstTypeValue);
          setCompanyLogoUrl(companyData.company_logo || null);
        } else {
          alert(response?.data?.message || "No company data found");
        }
      } catch (error: any) {
        console.error("Error fetching company details:", error);
        alert(
          error?.response?.data?.message || "Failed to fetch company details"
        );
      }
    };

    fetchCompanyDetails();
  }, [token, company_id, gstTypes]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(formattedValue);
    if (!formattedValue) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "Phone number is required",
      }));
    } else if (formattedValue.length !== 10) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "Phone number must be exactly 10 digits",
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone_number: undefined }));
    }
  };

  const handleContactNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setContactNumber(formattedValue);
    if (!formattedValue) {
      setErrors((prev) => ({
        ...prev,
        contact_number: "Contact number is required",
      }));
    } else if (formattedValue.length !== 10) {
      setErrors((prev) => ({
        ...prev,
        contact_number: "Contact number must be exactly 10 digits",
      }));
    } else {
      setErrors((prev) => ({ ...prev, contact_number: undefined }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof errors
  ) => {
    const value = e.target.value;
    switch (field) {
      case "company_name":
        setCompanyName(value);
        setErrors((prev) => ({
          ...prev,
          company_name: value.trim() === "" ? "Company name is required" : "",
        }));
        break;
      case "email":
        setEmail(value);
        setErrors((prev) => ({
          ...prev,
          email:
            value && !isValidEmail(value)
              ? "Please enter a valid email address"
              : value.trim() === ""
              ? "Email is required"
              : "",
        }));
        break;
      case "address":
        setAddress(value);
        setErrors((prev) => ({
          ...prev,
          address: value.trim() === "" ? "Address is required" : "",
        }));
        break;
    }
  };

  const handleGstinChange = (
    gstType: { id: string; type_name: string } | null
  ) => {
    const value = gstType ? gstType.id : "";
    setGstType(value);
    setErrors((prev) => ({
      ...prev,
      gst_type: value ? "" : "GST type is required",
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          company_logo: "File size must be less than 5MB",
        }));
        return;
      }
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          company_logo: "Only PNG or JPEG files are allowed",
        }));
        return;
      }
      setCompanyLogo(file);
      setCompanyLogoUrl(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, company_logo: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    const newErrors: typeof errors = {};

    if (!company_name) newErrors.company_name = "Company name is required";
    if (!email) newErrors.email = "Email is required";
    else if (!isValidEmail(email))
      newErrors.email = "Please enter a valid email address";
    if (!phone_number) newErrors.phone_number = "Phone number is required";
    else if (phone_number.length !== 10)
      newErrors.phone_number = "Phone number must be exactly 10 digits";
    if (!contact_number)
      newErrors.contact_number = "Contact number is required";
    else if (contact_number.length !== 10)
      newErrors.contact_number = "Contact number must be exactly 10 digits";
    if (!address) newErrors.address = "Address is required";
    if (!gst_type) newErrors.gst_type = "GST type is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      id: company_id,
      company_name,
      email,
      phone_number,
      contact_number,
      address,
      gst_type,
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, value);
    });
    if (company_logo) {
      formData.append("company_logo", company_logo);
    }

    if (!token) {
      alert("Authentication error! Please log in.");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/companies/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          if (onClose) onClose();
          navigate("/company-details");
        }, 2000);
      } else {
        alert(response?.data?.message || "Unexpected server response");
      }
    } catch (error: any) {
      console.error("API Error:", error);
      alert(error?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="w-[90%] max-w-[600px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-left relative"
      >
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Edit Company
        </h4>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-white block mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={company_name}
              onChange={(e) => handleChange(e, "company_name")}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.company_name
                  ? "border-red-500"
                  : company_name
                  ? "border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-500/10"
                  : "border-gray-300"
              }`}
            />
            {errors.company_name && (
              <p className="text-red-500 text-xs">{errors.company_name}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-white block mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => handleChange(e, "email")}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.email
                    ? "border-red-500"
                    : email && isValidEmail(email)
                    ? "border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-500/10"
                    : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs">{errors.email}</p>
              )}
            </div>
            <div>
              <ListboxComponent
                value={gstTypes.find((type) => type.id === gst_type) ?? null}
                onChange={handleGstinChange}
                items={gstTypes}
                label="GST Type"
                displayKey="type_name"
                error={errors.gst_type}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-white block mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                value={phone_number}
                onChange={handlePhoneNumberChange}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.phone_number
                    ? "border-red-500"
                    : phone_number.length === 10
                    ? "border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-500/10"
                    : "border-gray-300"
                }`}
              />
              {errors.phone_number && (
                <p className="text-red-500 text-xs">{errors.phone_number}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-white block mb-1">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                value={contact_number}
                onChange={handleContactNumberChange}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.contact_number
                    ? "border-red-500"
                    : contact_number.length === 10
                    ? "border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-500/10"
                    : "border-gray-300"
                }`}
              />
              {errors.contact_number && (
                <p className="text-red-500 text-xs">{errors.contact_number}</p>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-white block mb-1">
              Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={address}
              onChange={(e) => handleChange(e, "address")}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/10 transition-colors ${
                errors.address
                  ? "border-red-500"
                  : address
                  ? "border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-500/10"
                  : "border-gray-300"
              }`}
            />
            {errors.address && (
              <p className="text-red-500 text-xs">{errors.address}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-1">
                Company Logo
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoChange}
                className={`w-full px-3 py-2 border rounded-md text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors ${
                  errors.company_logo
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              />
              {errors?.company_logo && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                  {errors?.company_logo}
                </p>
              )}
            </div>
            <div className="flex-1">
              {company_logo_url && (
                <div className="mt-6 sm:mt-0">
                  <p className="text-sm font-medium dark:text-gray-200 mb-1">
                    Logo Preview
                  </p>
                  <img
                    src={company_logo_url}
                    alt="Company Logo"
                    className="w-24 h-24 object-contain rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 p-2"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="submit"
              className="px-6 py-2 text-sm bg-[#2f6aa7] dark:bg-[#3d6794] text-white rounded-lg hover:bg-[#2779bd] dark:hover:bg-[#335877] w-auto"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-white text-gray-700 ring-1 ring-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600 w-auto"
            >
              Cancel  
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
          Company Updated Successfully
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The company details have been updated successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default EditCompanyModal;
